
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';

import './LeftSidebar.css';

import SidebarMenuDropdown from '../leftSidebar/dropdowns/SidebarMenuDropdown';

import contalearn from '../../../header/images/logo.png';
import clasamenticon from '../images/clasamenticon.png';
import certificates from '../images/certificates.png';
import misiune from '../images/misiune.png';
import moreicon from '../images/moreicon.png';
import homeicon from '../images/homeicon.png';
import smallLogo from '../images/logoconta.png';

import AvatarPreview from './pages/profile/avatar/AvatarPreview';
import { defaultAvatar } from './pages/profile/avatar/avatarConfig';

const apiUrl = process.env.REACT_APP_API_URL;

const LeftSidebar = ({
  onMenuSelect,
  selectedMenu,
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [userAvatar, setUserAvatar] = useState(defaultAvatar);

  const moreButtonRef = useRef(null);
  const dropdownTimeout = useRef(null);

  // ==========================================
  // AVATAR
  // ==========================================

  useEffect(() => {
    const fetchUserAvatar = async () => {
      try {
        const response = await fetch(`${apiUrl}/user`, {
          credentials: 'include',
        });

        if (!response.ok) return;

        const user = await response.json();

        if (
          user?.avatar_config &&
          typeof user.avatar_config === 'object'
        ) {
          setUserAvatar({
            ...defaultAvatar,
            ...user.avatar_config,
          });
        }
      } catch (error) {
        console.error('Eroare incarcare avatar:', error);
      }
    };

    fetchUserAvatar();
  }, []);

  useEffect(() => {
    const handleAvatarUpdated = (event) => {
      if (
        event.detail &&
        typeof event.detail === 'object'
      ) {
        setUserAvatar({
          ...defaultAvatar,
          ...event.detail,
        });
      }
    };

    window.addEventListener(
      'avatarUpdated',
      handleAvatarUpdated
    );

    return () => {
      window.removeEventListener(
        'avatarUpdated',
        handleAvatarUpdated
      );
    };
  }, []);

  // ==========================================
  // POZITIONARE DROPDOWN
  // ==========================================

  const updateDropdownPosition = useCallback(() => {
    if (!moreButtonRef.current) return;

    const rect = moreButtonRef.current.getBoundingClientRect();

    const dropdownWidth = 190;
    const dropdownHeight = 225;
    const margin = 10;

    // Panoul incepe imediat dupa marginea dreapta
    // a sidebarului / butonului.
    const left = rect.right;

    // Aliniere la partea de sus a butonului.
    // Ajustam doar daca nu incape pe verticala.
    const top = Math.max(
      margin,
      Math.min(
        rect.top,
        window.innerHeight - dropdownHeight - margin
      )
    );

    setDropdownPosition({
      top,
      left,
      width: Math.min(
        dropdownWidth,
        Math.max(0, window.innerWidth - left - margin)
      ),
    });
  }, []);

  useEffect(() => {
    if (!dropdownVisible) return;

    updateDropdownPosition();

    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [dropdownVisible, updateDropdownPosition]);

  // ==========================================
  // DESCHIDERE / INCHIDERE DROPDOWN
  // ==========================================

  const cancelCloseDropdown = () => {
    if (dropdownTimeout.current) {
      clearTimeout(dropdownTimeout.current);
      dropdownTimeout.current = null;
    }
  };

  const handleMoreEnter = () => {
    cancelCloseDropdown();
    updateDropdownPosition();
    setDropdownVisible(true);
  };

  const handleMoreLeave = () => {
    cancelCloseDropdown();

    dropdownTimeout.current = setTimeout(() => {
      setDropdownVisible(false);
      dropdownTimeout.current = null;
    }, 250);
  };

  const handleDropdownEnter = () => {
    cancelCloseDropdown();
  };

  const handleDropdownLeave = () => {
    handleMoreLeave();
  };

  const handleDropdownSelect = (menu) => {
    cancelCloseDropdown();
    setDropdownVisible(false);
    onMenuSelect?.(menu);
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        cancelCloseDropdown();
        setDropdownVisible(false);
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (dropdownTimeout.current) {
        clearTimeout(dropdownTimeout.current);
      }
    };
  }, []);

  return (
    <>
      {/* SIDEBAR DESKTOP */}

      <div className="sidebar-menu-principal sidebar-menu-desktop">
        <div className="sidebar-logo">
          <img
            src={contalearn}
            className="logo-functional-page logo-large"
            alt="ContaLearn"
          />

          <img
            src={smallLogo}
            className="logo-functional-page logo-small"
            alt="ContaLearn"
          />
        </div>

        <ul className="sidebar-list">
          <li
            className={`sidebar-item${selectedMenu === 'Invata' ? ' active' : ''}`}
            onClick={() => onMenuSelect?.('Invata')}
          >
            <span className="sidebar-icon">
              <img src={homeicon} className="icon-functional-page" alt="" />
            </span>
            <span className="sidebar-label">Invata</span>
          </li>

          <li
            className={`sidebar-item${selectedMenu === 'Misiuni' ? ' active' : ''}`}
            onClick={() => onMenuSelect?.('Misiuni')}
          >
            <span className="sidebar-icon">
              <img src={misiune} className="icon-functional-page" alt="" />
            </span>
            <span className="sidebar-label">Misiuni</span>
          </li>

          <li
            className={`sidebar-item${selectedMenu === 'Clasament' ? ' active' : ''}`}
            onClick={() => onMenuSelect?.('Clasament')}
          >
            <span className="sidebar-icon">
              <img src={clasamenticon} className="icon-functional-page" alt="" />
            </span>
            <span className="sidebar-label">Clasament</span>
          </li>

          <li
            className={`sidebar-item${selectedMenu === 'Certificate' ? ' active' : ''}`}
            onClick={() => onMenuSelect?.('Certificate')}
          >
            <span className="sidebar-icon">
              <img src={certificates} className="icon-functional-page" alt="" />
            </span>
            <span className="sidebar-label">Certificate</span>
          </li>

          <li
            className={`sidebar-item${selectedMenu === 'Profil' ? ' active' : ''}`}
            onClick={() => onMenuSelect?.('Profil')}
          >
            <span className="sidebar-icon sidebar-profile-avatar">
              <AvatarPreview
                avatar={userAvatar}
                className="avatar-preview-sidebar"
              />
            </span>
            <span className="sidebar-label">Profil</span>
          </li>

          {/* MAI MULTE */}

          <li
            ref={moreButtonRef}
            className={`sidebar-item sidebar-more-trigger${dropdownVisible ? ' dropdown-open' : ''}`}
            onMouseEnter={handleMoreEnter}
            onMouseLeave={handleMoreLeave}
            onClick={() => {
              if (dropdownVisible) {
                cancelCloseDropdown();
                setDropdownVisible(false);
              } else {
                handleMoreEnter();
              }
            }}
            role="button"
            tabIndex={0}
            aria-haspopup="menu"
            aria-expanded={dropdownVisible}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();

                if (dropdownVisible) {
                  cancelCloseDropdown();
                  setDropdownVisible(false);
                } else {
                  handleMoreEnter();
                }
              }
            }}
          >
            <span className="sidebar-icon">
              <img
                src={moreicon}
                className="icon-functional-page"
                alt=""
              />
            </span>

            <span className="sidebar-label">
              Mai multe
            </span>
          </li>
        </ul>
      </div>

      {/* DROPDOWN RENDERIZAT SEPARAT */}

      <SidebarMenuDropdown
        visible={dropdownVisible && dropdownPosition !== null}
        position={dropdownPosition}
        selectedMenu={selectedMenu}
        onMenuSelect={handleDropdownSelect}
        onMouseEnter={handleDropdownEnter}
        onMouseLeave={handleDropdownLeave}
        onClose={() => {
          cancelCloseDropdown();
          setDropdownVisible(false);
        }}
      />

      {/* NAVIGATIE MOBIL */}

      <nav className="sidebar-menu-bottom">
        <ul className="sidebar-bottom-list">
          <li
            className={`sidebar-bottom-item${selectedMenu === 'Invata' ? ' active' : ''}`}
            onClick={() => onMenuSelect?.('Invata')}
          >
            <span className="sidebar-icon">
              <img src={homeicon} className="icon-functional-page" alt="" />
            </span>
          </li>

          <li
            className={`sidebar-bottom-item${selectedMenu === 'Misiuni' ? ' active' : ''}`}
            onClick={() => onMenuSelect?.('Misiuni')}
          >
            <span className="sidebar-icon">
              <img src={misiune} className="icon-functional-page" alt="" />
            </span>
          </li>

          <li
            className={`sidebar-bottom-item${selectedMenu === 'Clasament' ? ' active' : ''}`}
            onClick={() => onMenuSelect?.('Clasament')}
          >
            <span className="sidebar-icon">
              <img src={clasamenticon} className="icon-functional-page" alt="" />
            </span>
          </li>

          <li
            className={`sidebar-bottom-item${selectedMenu === 'Certificate' ? ' active' : ''}`}
            onClick={() => onMenuSelect?.('Certificate')}
          >
            <span className="sidebar-icon">
              <img src={certificates} className="icon-functional-page" alt="" />
            </span>
          </li>

          <li
            className={`sidebar-bottom-item${selectedMenu === 'Profil' ? ' active' : ''}`}
            onClick={() => onMenuSelect?.('Profil')}
          >
            <span className="sidebar-icon sidebar-profile-avatar">
              <AvatarPreview
                avatar={userAvatar}
                className="avatar-preview-sidebar"
              />
            </span>
          </li>

          <li className="sidebar-item-mai-multe">
            <span className="sidebar-icon">
              <img src={moreicon} className="icon-functional-page" alt="" />
            </span>
          </li>
        </ul>
      </nav>
    </>
  );
};

export default LeftSidebar;
